"use client";

import {
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useMemo,
} from "react";
import { mergeClasses, mergeEvents } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { motion, type MotionProps } from "motion/react";

import {
  type ApplyMotionProp,
  type BorderProp,
  type EdgesProps,
  type ElevatedProp,
  type OverflowHiddenProp,
  type PolyForwardComponent,
  polymorphicForwardRef,
} from "../../props";
import {
  cardComponentStyle,
  cardContentStyle,
  cardContainerStyle,
  cardStyle,
} from "./Card.css";
import { Flex } from "../Flex/Flex";
import { borders } from "../../theme/css/borders.css";
import { elevations } from "../../theme/css/elevations.css";
import { typography } from "../../theme/css/typography.css";
import { Edges } from "../Edges/Edges";
import { cardCollectionChildMotionVariants } from "../../config/cardCollectionMotion";
import { useHover } from "../../hooks/useHover";
import { ParentHoverContext } from "../../contexts/ParentHoverContext";

export type CardProps = Omit<HTMLAttributes<HTMLElement>, "title"> &
  EdgesProps &
  ElevatedProp &
  BorderProp &
  OverflowHiddenProp &
  ApplyMotionProp &
  RecipeVariants<typeof cardContentStyle> &
  RecipeVariants<typeof cardContainerStyle> & {
    edgeDirection?: "horizontal" | "vertical";
    direction?: "horizontal" | "vertical" | "responsiveHorizontal";
    columns?: "one" | "two";
    padding?: "none" | "default" | "xs" | "s" | "l" | "xl";
    gap?: "xs" | "s" | "m" | "l" | null | "none";
    href?: string;
    disabled?: boolean;
    elevatedOnHover?: boolean;
    innerClassName?: string;
    contentClassName?: string;
    trailingFinal?: ReactNode;
    onHoverChange?: (hasHover: boolean) => void;
    borderRadius?: "none" | "xs" | "s" | "m" | "l";
  };

/**
 * A card is a container for content, typically with a border and padding.
 * @todo Tracking
 */
export const Card = polymorphicForwardRef<"div", CardProps>(
  (
    {
      as: Element = "div",
      variant = "primary",
      direction = "vertical",
      edgeDirection = "vertical",
      elevated = false,
      elevatedOnHover = false,
      disabled = false,
      padding = "default",
      columns,
      overflowHidden = false,
      gap,
      applyMotion = false,
      border,
      applyPadding = true,
      trailing,
      trailingFinal,
      trailingClassName,
      leading,
      leadingClassName,
      innerClassName,
      contentClassName,
      className,
      children,
      onMouseEnter,
      onMouseLeave,
      onHoverChange,
      borderRadius,
      ...props
    },
    ref,
  ) => {
    const { handleEnter, handleLeave, hasHover } = useHover();

    const isInteractive = useMemo(
      () => !!props.onClick || !!props.href || Element === "button",
      [props.onClick],
    );

    const MotionElement = (motion[Element as keyof typeof motion] ||
      motion.div) as PolyForwardComponent<
      "div",
      CardProps & MotionProps,
      ElementType
    >;

    useEffect(() => {
      if (isInteractive && onHoverChange) {
        onHoverChange?.(hasHover);
      }
    }, [hasHover, isInteractive]);

    return (
      <MotionElement
        ref={ref}
        className={mergeClasses(
          cardStyle,
          cardContainerStyle({
            variant,
            columns,
            disabled,
          }),
          typography.bodyM,
          className,
        )}
        onMouseEnter={mergeEvents(handleEnter, onMouseEnter)}
        onMouseLeave={mergeEvents(handleLeave, onMouseLeave)}
        variants={applyMotion ? cardCollectionChildMotionVariants : undefined}
        initial={applyMotion ? "hidden" : undefined}
        animate={applyMotion ? "visible" : undefined}
        exit={applyMotion ? "exit" : undefined}
        {...(disabled && Element === "button" && { disabled })}
        {...(Element === "button" && { type: "button" })}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(props as any)}
      >
        <ParentHoverContext.Provider
          value={{ parentHover: isInteractive && hasHover }}
        >
          <Flex
            className={mergeClasses(
              cardComponentStyle({
                variant,
                direction,
                overflowHidden:
                  border === "gradient" || border === "gradientAnimated"
                    ? false
                    : overflowHidden,
                hoverable: isInteractive && !disabled,
                padding,
                borderRadius,
              }),
              elevated
                ? elevations.card
                : elevatedOnHover && hasHover && isInteractive
                  ? elevations.card
                  : undefined,
              border ? borders[border] : undefined,
              innerClassName,
            )}
          >
            <Edges
              trailing={trailing}
              trailingClassName={trailingClassName}
              leading={leading}
              leadingClassName={leadingClassName}
              className={mergeClasses(
                cardContentStyle({
                  direction: edgeDirection,
                  variant,
                  gap,
                  applyBackground:
                    border === "gradient" || border === "gradientAnimated",
                  applyPadding,
                }),
                contentClassName,
              )}
            >
              {children}
            </Edges>
          </Flex>
        </ParentHoverContext.Provider>
        {trailingFinal}
      </MotionElement>
    );
  },
);
